import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import invoiceApi from 'api/invoiceApi'
import { setAppSpinning } from 'app/appSlice'
import {
  setClearRows,
  setSelectedInvoice,
  setSelectedInvoices,
} from 'features/Invoice/invoiceSlice'
import { saveAs } from 'file-saver'
import { INVOICE_STATUS } from 'general/constants/AppConstants'
import ToastHelper from 'general/helpers/ToastHelper'
import Utils, { getInvoiceDownloadFileName } from 'general/utils/Utils'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import JSZip from 'jszip'
import _ from 'lodash'
import { useCallback } from 'react'

export const listInvoiceFromPosQueries = {
  base: {
    scope: ['invoice', 'listInvoiceFromPOS'],
  },
  listInvoice: (params) => {
    const paramsWithoutSearchFields = _.omit(params, 'searchFields')
    const { q, searchValue, searchField, ...baseParams } =
      paramsWithoutSearchFields
    const queryParams =
      searchField && searchValue?.trim()
      ? { ...baseParams, searchValue: searchValue?.trim() ?? '', searchField }
      : { ...baseParams, q: q?.trim() ?? '' }

    return queryOptions({
      queryKey: [
        listInvoiceFromPosQueries.base,
        { entity: 'listInvoice' },
        queryParams,
      ],
      queryFn: ({ signal }) => invoiceApi.listAll(queryParams, signal),
      select: (res) => ({
        reports: res.reports ?? [],
        total: _.get(res, 'count', 0),
        dataInvoiceLicensePlate: _.get(res, 'dataInvoiceLicensePlate', {}),
        tableData: !res.rows
          ? []
          : _.map(res.rows, (row, idx) => ({
              ...row,
              stt: idx + 1,
              no: row?.no ? Utils.padString(row.no, 8, '0') : '',
              // isSent: row.isSent === 1,
            })),
      }),
    })
  },
  listInvoiceMttNeedSendTax: (params) => {
    const { enabled, ...requestParams } = params
    const queryParams = _.omit(requestParams, 'searchFields')

    return queryOptions({
      queryKey: [
        listInvoiceFromPosQueries.base,
        { entity: 'listInvoiceMttNeedSendTax' },
        queryParams,
      ],
      queryFn: ({ signal }) =>
        invoiceApi.getInvoiceMttNeedSendTax(queryParams, signal),
      enabled,
      staleTime: 30 * 1000,
      select: (res) => ({
        invoices: !res.data
          ? []
          : _.map(res.data.invoices, (row, idx) => ({
              ...row,
              stt: idx + 1,
              no: row?.no ? Utils.padString(row.no, 8, '0') : '',
              // isSent: row.isSent === 1,
            })),
        invoicePagination: res.data?.invoicePagination,
        tickets: !res.data
          ? []
          : _.map(res.data.tickets, (row, idx) => ({
              ...row,
              stt: idx + 1,
              no: row?.no ? Utils.padString(row.no, 8, '0') : '',
              isSent: row.isSent === 1,
            })),
        ticketPagination: res.data?.ticketPagination,
      }),
    })
  },
}

export const useMutationViewPdf = () => {
  return useMutation({
    mutationKey: [listInvoiceFromPosQueries.base, { action: 'viewInvoice' }],
    mutationFn: (params) => invoiceApi.exportInvoiceById(params),
    onSuccess: (res) => {
      if (_.isNil(res)) return
      const fileURL = res.metadata?.fileURL
      const url = fileURL
        ? /^https?:\/\//.test(fileURL)
          ? fileURL
          : `${process.env.REACT_APP_BASE_URL}${fileURL}`
        : ''
      window.open(url, '_blank')
    },
  })
}

export const useMutationViewAllPdf = () => {
  return useMutation({
    mutationKey: [listInvoiceFromPosQueries.base, { action: 'viewAllInvoice' }],
    mutationFn: (params) => invoiceApi.print(params),
    onSuccess: (res) => {
      if (_.isNil(res)) return
      const fileURL = res.metadata?.fileURL
      const url = fileURL
        ? /^https?:\/\//.test(fileURL)
          ? fileURL
          : `${process.env.REACT_APP_BASE_URL}${fileURL}`
        : ''
      window.open(url, '_blank')
    },
  })
}

export const useMutationInvoicePaidStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [
      listInvoiceFromPosQueries.base,
      { action: 'updatePaidStatus' },
    ],
    mutationFn: ({ invoiceId, isPaid }) =>
      invoiceApi.updatePaidStatus(invoiceId, { isPayment: isPaid }),
    onError: (err) => console.error(err.message),
    onSuccess: (res) => {
      if (res.result === 'success' || res.status === 200) {
        ToastHelper.showSuccess('Cập nhật trạng thái thanh toán thành công')
        queryClient.invalidateQueries({
          queryKey: [listInvoiceFromPosQueries.base, { entity: 'listInvoice' }],
        })
      }
    },
  })
}

export const useMutationBulkInvoicePaidStatus = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  return useMutation({
    mutationKey: [
      listInvoiceFromPosQueries.base,
      { action: 'bulk-updatePaidStatus' },
    ],
    mutationFn: ({ invoiceIds, isPayment }) =>
      invoiceApi.bulkUpdatePaidStatus({ invoiceIds, isPayment }),
    onError: (err) => console.error(err.message),
    onSuccess: (res) => {
      if (res.result !== 'success') {
        ToastHelper.showError('Cập nhật trạng thái thanh toán thất bại')
        return
      }
      dispatch(setSelectedInvoices([]))
      dispatch(setClearRows())
      ToastHelper.showSuccess('Cập nhật trạng thái thanh toán thành công')
      queryClient.invalidateQueries({
        queryKey: [listInvoiceFromPosQueries.base, { entity: 'listInvoice' }],
      })
    },
  })
}

export const useMutationDeleteInvoice = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  return useMutation({
    mutationKey: [listInvoiceFromPosQueries.base, { action: 'deleteInvoice' }],
    mutationFn: ({ invoices }) => {
      if (_.isEmpty(invoices) || _.isNil(invoices)) {
        ToastHelper.showError('Các hoá đơn đã chọn không thể xoá')
        return
      }

      if (invoices.length === 1) {
        const hasNotSentToAuthorityStatus =
          _.every(invoices, {
            issueStatus: INVOICE_STATUS.NOT_SENT_TO_AUTHORITY,
          }) ||
          _.every(invoices, {
            issueStatus: INVOICE_STATUS.AUTHORITY_CODE_NOT_GIVEN,
          }) ||
          _.every(invoices, {
            issueStatus: INVOICE_STATUS.AUTHORITY_DENY,
          }) ||
          _.every(invoices, {
            issueStatus: INVOICE_STATUS.SEND_ERROR,
          }) ||
          _.every(invoices, {
            issueStatus: INVOICE_STATUS.SIGN_ERROR,
          })

        if (!hasNotSentToAuthorityStatus) {
          ToastHelper.showError('Chỉ được phép xóa các hóa đơn chưa phát hành')
          return
        }
      }
      const ids = _.map(invoices, 'invoiceId')
      return invoiceApi.delete(ids)
    },
    onError: (err) => console.error(err.message),
    onSuccess: (res) => {
      if (res.result === 'success' || res.status === 200) {
        ToastHelper.showSuccess('Xoá thành công các hoá đơn chưa phát hành')
        dispatch(setSelectedInvoice(null))
        dispatch(setSelectedInvoices([]))
        queryClient.invalidateQueries({
          queryKey: [listInvoiceFromPosQueries.base, { entity: 'listInvoice' }],
        })
      }
    },
  })
}

export const useMutationDownloadInvoice = () => {
  const dispatch = useAppDispatch()
  const taxCode = useAppSelector((state) => state?.system?.company?.taxCode)
  const { mutate: handleDownloadPdf, data } = useMutation({
    mutationKey: [listInvoiceFromPosQueries.base, { action: 'download-pdf' }],
    mutationFn: async ({ invoiceId, serial, no, date }) => {
      const res = await invoiceApi.exportInvoiceById(invoiceId)
      return { res, serial, no, date }
    },
    onMutate: () => dispatch(setAppSpinning(true)),
    onSettled: () => dispatch(setAppSpinning(false)),
    onSuccess: async ({ res, serial, no, date }) => {
      if (_.isNil(res)) return
      const fileName = getInvoiceDownloadFileName({
        taxCode,
        serial,
        no,
        date,
        extension: 'pdf',
      })
      const fileURL = res.metadata?.fileURL
      const url = fileURL
        ? /^https?:\/\//.test(fileURL)
          ? fileURL
          : `${process.env.REACT_APP_BASE_URL}${fileURL}`
        : ''
      if (!url) return
      const response = await fetch(url)
      if (!response.ok) throw new Error('Không thể tải file PDF')
      const blobUrl = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    },
  })

  const handleDownloadXml = useCallback(
    async ({ xmlString, serial, no, date }) => {
      try {
        const fileName = getInvoiceDownloadFileName({
          taxCode,
          serial,
          no,
          date,
          extension: 'xml',
        })

        if (!xmlString) {
          ToastHelper.showError('Không tìm thấy dữ liệu XML')
          return
        }

        const trimmedXmlString =
          typeof xmlString === 'string' ? xmlString.trim() : xmlString

        const isXmlUrl =
          typeof trimmedXmlString === 'string' &&
          /^(https?:\/\/)/i.test(trimmedXmlString)

        if (isXmlUrl) {
          const response = await fetch(trimmedXmlString)

          if (!response.ok) {
            throw new Error('Lỗi khi tải tệp XML từ đường dẫn')
          }

          const xmlText = await response.text()

          if (!xmlText.trim().startsWith('<')) {
            throw new Error('Dữ liệu tải về không phải XML')
          }

          saveAs(
            new Blob([xmlText], { type: 'application/xml;charset=utf-8' }),
            fileName,
          )

          return
        }

        saveAs(
          new Blob([trimmedXmlString], {
            type: 'application/xml;charset=utf-8',
          }),
          fileName,
        )
      } catch (err) {
        console.error('handleDownloadXml error:', err)
        ToastHelper.showError('Có lỗi khi tải hóa đơn XML')
      }
    },
    [taxCode],
  )

  const handleDownloadZip = useCallback(
    async (xmlString) => {
      const zip = new JSZip()
      const pdfData = data
      zip.file('invoice.pdf', pdfData)
      zip.file('invoice.xml', xmlString)
      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, 'invoice.zip')
    },
    [data],
  )

  return {
    handleDownloadPdf,
    handleDownloadXml,
    handleDownloadZip,
  }
}

export const useMutationSendExplanationAnnounce = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [
      listInvoiceFromPosQueries.base,
      { action: 'sendExplanationAnnounce' },
    ],
    mutationFn: ({ customerEmailCc, customerEmailBcc, invoiceId, ...others }) =>
      invoiceApi.sendExplanationAnnounceToCustomer(invoiceId, {
        ...others,
        ccEmails: _.compact(_.split(customerEmailCc, ';')),
        bccEmails: _.compact(_.split(customerEmailBcc, ';')),
      }),
    onError: (err) => console.error(err.message),
    onSuccess: (res) => {
      if (res?.result === 'success') {
        ToastHelper.showSuccess(
          'Gửi thông báo giải trình cho khách hàng thành công',
        )
        queryClient.invalidateQueries({
          queryKey: [listInvoiceFromPosQueries.base, { entity: 'listInvoice' }],
        })
      }
    },
  })
}
